import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react"
import icon_autoRotate from "./assets/autoRotate.svg"
import icon_screenshot from "./assets/screenshot.svg"

// Funzione di supporto per scaricare il file base64 come immagine
function downloadBase64File(contentBase64, fileName) {
  const downloadLink = document.createElement("a")
  downloadLink.href = contentBase64
  downloadLink.target = "_self"
  downloadLink.download = fileName
  document.body.appendChild(downloadLink)
  downloadLink.click()
  document.body.removeChild(downloadLink)
}

const PlannerPlayer = forwardRef(
  ({ onLoad, iframeRef, floorMaterial, setFloorMaterial }, ref) => {
    const emersyaIframeRef = useRef(null)
    const [emersyaViewerInstance, setEmersyaViewerInstance] = useState(null)
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

    // Elenco dei materiali disponibili per il pavimento
    const floorMaterials = ["piano", "resina", "legno", "floor b", "floor c"]

    // Ref to the floor toggle button so we can click it programmatically
    const floorButtonRef = useRef(null)

    // Expose functions to parent - clicking the actual button to ensure same behavior
    useImperativeHandle(
      ref,
      () => ({
        setFloorMaterialDirect: (targetMaterial) => {
          if (!floorMaterials.includes(targetMaterial)) {
            console.warn(
              "[PlannerPlayer] Unknown floor material:",
              targetMaterial,
            )
            return false
          }

          const targetIndex = floorMaterials.indexOf(targetMaterial)
          const currentIndex = floorMaterials.indexOf(floorMaterial)

          if (targetIndex === currentIndex) {
            console.log(
              "[PlannerPlayer] Already at target material:",
              targetMaterial,
            )
            // Already at target, nothing to do
            return true
          }

          // Calculate how many clicks needed
          let clicksNeeded = targetIndex - currentIndex
          if (clicksNeeded < 0) {
            clicksNeeded += floorMaterials.length
          }

          console.log(
            `[PlannerPlayer] Clicking floor button ${clicksNeeded} times to reach ${targetMaterial}`,
          )

          // Click the actual button multiple times
          if (floorButtonRef.current) {
            for (let i = 0; i < clicksNeeded; i++) {
              setTimeout(() => {
                floorButtonRef.current?.click()
              }, i * 200)
            }
          }

          return true
        },
        getFloorMaterials: () => floorMaterials,
      }),
      [floorMaterial, floorMaterials],
    )

    useEffect(() => {
      // Check if the script is already loaded
      if (!document.getElementById("emersya")) {
        // Carichiamo lo script di Emersya
        const script = document.createElement("script")
        script.type = "text/javascript"
        script.src = "https://cdn.emersya.com/f/emersyaLoader.js"
        script.id = "emersya"
        document.head.appendChild(script)
      }

      // Handler per l'inizializzazione del viewer
      const handleViewerInitialized = (event) => {
        console.log("Emersya viewer initialization event received")

        // Check if the event is for our iframe
        if (window.emViewers && window.emViewers["emersyaIframe"]) {
          console.log("Emersya viewer initialized successfully")
          setEmersyaViewerInstance(window.emViewers["emersyaIframe"])
        }
      }

      // Listener per l'evento di inizializzazione
      document.addEventListener(
        "emersyaViewerInitialized",
        handleViewerInitialized,
        false,
      )

      // Check if the viewer is already available
      if (window.emViewers && window.emViewers["emersyaIframe"]) {
        console.log("Emersya viewer already available on component mount")
        setEmersyaViewerInstance(window.emViewers["emersyaIframe"])
      }

      // Poll for viewer existence for a short time
      const checkInterval = setInterval(() => {
        if (window.emViewers && window.emViewers["emersyaIframe"]) {
          console.log("Emersya viewer found through polling")
          setEmersyaViewerInstance(window.emViewers["emersyaIframe"])
          clearInterval(checkInterval)
        }
      }, 1000)

      // Stop polling after 10 seconds
      setTimeout(() => clearInterval(checkInterval), 10000)

      return () => {
        const script = document.getElementById("emersya")
        if (script && script.parentNode) {
          script.parentNode.removeChild(script)
        }
        document.removeEventListener(
          "emersyaViewerInitialized",
          handleViewerInitialized,
        )
        clearInterval(checkInterval)
      }
    }, [])

    // Cambio materiale pavimento
    const toggleFloorMaterial = useCallback(() => {
      if (!iframeRef.current) return

      const currentIndex = floorMaterials.indexOf(floorMaterial)
      const nextIndex = (currentIndex + 1) % floorMaterials.length
      const nextMaterial = floorMaterials[nextIndex]

      iframeRef.current.contentWindow.postMessage(
        {
          action: "setMaterials",
          materials: [
            {
              materialVariation: nextMaterial,
              configurableMaterial: "DefaultMaterial",
            },
          ],
        },
        "*",
      )

      setFloorMaterial(nextMaterial)
      console.log(nextMaterial)
    }, [floorMaterial, floorMaterials, iframeRef])

    // Play rotation
    const playRotation = useCallback(() => {
      if (!iframeRef.current) return
      iframeRef.current.contentWindow.postMessage(
        {
          action: "play",
        },
        "*",
      )
    }, [iframeRef])

    const zoomIn = useCallback(() => {
      if (!iframeRef.current) return
      iframeRef.current.contentWindow.postMessage(
        {
          action: "startZoomIn",
        },
        "*",
      )

      setTimeout(() => {
        if (!iframeRef.current) return
        iframeRef.current.contentWindow.postMessage(
          {
            action: "stopZoomIn",
          },
          "*",
        )
      }, 400)
    }, [iframeRef])

    const zoomOut = useCallback(() => {
      if (!iframeRef.current) return
      iframeRef.current.contentWindow.postMessage(
        {
          action: "startZoomOut",
        },
        "*",
      )

      setTimeout(() => {
        if (!iframeRef.current) return
        iframeRef.current.contentWindow.postMessage(
          {
            action: "stopZoomOut",
          },
          "*",
        )
      }, 400)
    }, [iframeRef])

    // Funzione getScreenshot migliorata con camera dinamica
    const getScreenshot = useCallback(() => {
      console.log("Taking screenshot with dynamic camera view")

      // Verifica che l'iframe esista
      if (!iframeRef.current || !iframeRef.current.contentWindow) {
        console.error("Iframe reference not available")
        return
      }

      // Prima otteniamo i dati della camera, poi facciamo lo screenshot
      let hasProcessedScreenshot = false

      // Handler per entrambe le risposte (camera e screenshot)
      const messageHandler = (event) => {
        const data = event.data
        console.log("Received message:", data?.action)

        // Se riceviamo i dati della camera
        if (data && data.action === "onGetCamera" && data.camera) {
          console.log("Camera data received:", data.camera)

          // Ora possiamo richiedere lo screenshot con i dati della camera
          iframeRef.current.contentWindow.postMessage(
            {
              action: "getScreenshot",
              width: 512,
              height: 512,
              transparentBackground: false,
              camera: {
                position: [
                  data.camera.position[0],
                  data.camera.position[1],
                  data.camera.position[2],
                ],
                target: [
                  data.camera.target[0],
                  data.camera.target[1],
                  data.camera.target[2],
                ],
                up: [data.camera.up[0], data.camera.up[1], data.camera.up[2]],
              },
            },
            "*",
          )
        }

        // Se riceviamo lo screenshot
        if (
          data &&
          data.action === "onScreenshot" &&
          data.screenshot &&
          !hasProcessedScreenshot
        ) {
          hasProcessedScreenshot = true // Previene doppi download
          console.log("Screenshot received!")

          // Crea nome file dinamico: Planner-YYYY-MM-DD-HHMMSS.png
          const now = new Date()
          const formattedDate = now
            .toISOString()
            .replace(/T/, "-")
            .replace(/\..+/, "")
            .replace(/:/g, "")
            .slice(0, 17)

          const fileName = `Planner-${formattedDate}.png`

          // Scarica l'immagine
          downloadBase64File(data.screenshot, fileName)

          // Rimuovi il listener dopo aver ricevuto lo screenshot
          window.removeEventListener("message", messageHandler)
          console.log("Listener removed after screenshot processing")
        }
      }

      // Aggiungiamo il listener per gestire entrambe le risposte
      window.addEventListener("message", messageHandler)

      // Prima richiediamo i dati della camera
      iframeRef.current.contentWindow.postMessage(
        {
          action: "getCamera",
        },
        "*",
      )

      // Timeout di sicurezza
      setTimeout(() => {
        window.removeEventListener("message", messageHandler)
        console.log("Removed handler after timeout")
      }, 10000) // 10 secondi per dare più tempo al processo
    }, [iframeRef])

    return (
      <div
        className="ay-relative ay-w-full ay-h-full"
      >
        {/* Iframe Emersya */}
        <iframe
          id="emersyaIframe"
          src="https://emersya.com/showcase/T9GISPYRWU"
          className="ay-w-full ay-h-full ay-border-none ay-absolute ay-top-0 ay-left-0"
          ref={(el) => {
            emersyaIframeRef.current = el
            if (iframeRef) iframeRef.current = el
          }}
          onLoad={onLoad}
        />

        {/* Desktop bottom center controls */}
        <div className="ay-absolute ay-left-1/2 ay-transform ay--translate-x-1/2 ay-bottom-8 ay-z-10 ay-hidden md:ay-flex ay-items-center ay-gap-3">
          {/* Main control bar - pill shaped */}
          <div className="ay-group/bar ay-flex ay-items-center ay-gap-1 md:ay-gap-1 ay-bg-white/95 ay-backdrop-blur-sm ay-px-2 md:ay-px-3 ay-py-1.5 md:ay-py-2 ay-rounded-full">
            {/* Floor Material Toggle */}
            <button
              ref={floorButtonRef}
              onClick={toggleFloorMaterial}
              className="ay-flex ay-items-center ay-h-7 md:ay-h-8 ay-w-10 md:ay-w-auto ay-px-1.5 md:ay-px-2 ay-rounded hover:ay-bg-black/5"
              style={{ transition: "background-color 0.15s ease" }}
              title={`Pavimento: ${floorMaterial}`}
            >
              <span className="ay-text-[8px] ay-font-bold ay-uppercase ay-leading-none ay-text-center ay-flex-shrink-0">
                FLOOR
              </span>
              <span className="ay-bar-label ay-text-[10px] ay-font-medium ay-uppercase ay-whitespace-nowrap ay-overflow-hidden ay-max-w-0 md:group-hover/bar:ay-max-w-[60px] md:group-hover/bar:ay-ml-1.5">
                {floorMaterial.toUpperCase()}
              </span>
            </button>

            {/* Zoom +/- (text) */}
            <div className="ay-flex ay-items-center ay-h-7 md:ay-h-8">
              <button
                onClick={zoomIn}
                className="ay-flex ay-items-center ay-justify-center ay-h-7 md:ay-h-8 ay-w-7 md:ay-w-8 ay-rounded-l hover:ay-bg-black/5 ay-text-lg md:ay-text-xl ay-font-light"
                style={{ transition: "background-color 0.15s ease" }}
                title="Zoom In"
              >
                +
              </button>
              <button
                onClick={zoomOut}
                className="ay-bar-label ay-flex ay-items-center ay-justify-center ay-h-7 md:ay-h-8 ay-w-7 md:ay-w-0 md:ay-overflow-hidden md:group-hover/bar:ay-w-8 ay-rounded-r hover:ay-bg-black/5 ay-text-lg md:ay-text-xl ay-font-light"
                title="Zoom Out"
              >
                −
              </button>
            </div>

            {/* Rotate */}
            <button
              onClick={playRotation}
              className="ay-flex ay-items-center ay-h-7 md:ay-h-8 ay-w-10 md:ay-w-auto ay-px-1.5 md:ay-px-2 ay-rounded hover:ay-bg-black/5"
              style={{ transition: "background-color 0.15s ease" }}
              title="Ruota"
            >
              <img
                src={icon_autoRotate}
                alt="Ruota"
                className="ay-w-5 ay-h-5 ay-flex-shrink-0"
              />
              <span className="ay-bar-label ay-text-[10px] ay-font-medium ay-uppercase ay-whitespace-nowrap ay-overflow-hidden ay-max-w-0 md:group-hover/bar:ay-max-w-[50px] md:group-hover/bar:ay-ml-1.5">
                RUOTA
              </span>
            </button>
          </div>

          {/* Extra buttons - only Screenshot */}
          <div className="ay-flex ay-items-center ay-gap-2">
            {/* Screenshot */}
            <button
              onClick={getScreenshot}
              className="ay-group/btn ay-flex ay-items-center ay-h-12 ay-bg-gray-100 ay-rounded-full hover:ay-bg-gray-200 ay-overflow-hidden"
              style={{ transition: "background-color 0.15s ease" }}
              title="Scarica Immagine"
            >
              <span className="ay-flex ay-items-center ay-justify-center ay-w-12 ay-h-12 ay-flex-shrink-0">
                <img
                  src={icon_screenshot}
                  alt="Screenshot"
                  className="ay-w-5 ay-h-5"
                />
              </span>
              <span className="ay-extra-label ay-text-[9px] ay-font-medium ay-uppercase ay-whitespace-nowrap ay-overflow-hidden ay-max-w-0 group-hover/btn:ay-max-w-[100px] group-hover/btn:ay-pr-4">
                SCARICA IMMAGINE
              </span>
            </button>
          </div>
        </div>

        {/* MOBILE: Expandable vertical menu - bottom left (like Player.js) */}
        <div
          className="md:ay-hidden ay-fixed ay-bottom-[calc(5vh+50px)] ay-left-4 ay-z-20 ay-cursor-pointer"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {/* Single unified container */}
          <div
            className="ay-shadow-xl ay-bg-white ay-overflow-hidden ay-flex ay-flex-col"
            style={{
              width: "56px",
              borderRadius: "28px",
              transition: isMobileMenuOpen
                ? "border-radius 0.3s ease 0s"
                : "border-radius 0.3s ease 0.5s",
            }}
          >
            {/* White section with icons */}
            <div
              className="ay-bg-white ay-flex ay-flex-col ay-items-center ay-overflow-hidden"
              style={{
                maxHeight: isMobileMenuOpen ? "500px" : "0px",
                paddingTop: isMobileMenuOpen ? "12px" : "0px",
                paddingBottom: isMobileMenuOpen ? "0px" : "0px",
                transition: isMobileMenuOpen
                  ? "max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0s, padding 0.3s ease 0s"
                  : "max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1) 0.3s, padding 0.2s ease 0.4s",
              }}
            >
              {/* Floor Material Toggle */}
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  toggleFloorMaterial()
                  setIsMobileMenuOpen(false)
                }}
                className="ay-flex ay-items-center ay-justify-center ay-w-11 ay-h-11 ay-rounded-full hover:ay-bg-gray-100"
                style={{
                  opacity: isMobileMenuOpen ? 1 : 0,
                  transform: isMobileMenuOpen ? "scale(1)" : "scale(0)",
                  transition: isMobileMenuOpen
                    ? "opacity 0.2s ease 0.30s, transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) 0.30s"
                    : "opacity 0.15s ease 0s, transform 0.2s ease 0s",
                }}
              >
                <span className="ay-text-[8px] ay-font-bold ay-uppercase ay-leading-none ay-text-center">
                  FLOOR
                </span>
              </button>

              {/* Rotate */}
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  playRotation()
                  setIsMobileMenuOpen(false)
                }}
                className="ay-flex ay-items-center ay-justify-center ay-w-11 ay-h-11 ay-rounded-full hover:ay-bg-gray-100"
                style={{
                  opacity: isMobileMenuOpen ? 1 : 0,
                  transform: isMobileMenuOpen ? "scale(1)" : "scale(0)",
                  transition: isMobileMenuOpen
                    ? "opacity 0.2s ease 0.20s, transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) 0.20s"
                    : "opacity 0.15s ease 0.06s, transform 0.2s ease 0.06s",
                }}
              >
                <img
                  src={icon_autoRotate}
                  alt="Ruota"
                  className="ay-w-5 ay-h-5"
                />
              </button>

              {/* Separator */}
              <div
                className="ay-w-7 ay-h-px ay-bg-gray-300 ay-my-2"
                style={{
                  opacity: isMobileMenuOpen ? 1 : 0,
                  transform: isMobileMenuOpen ? "scaleX(1)" : "scaleX(0)",
                  transition: isMobileMenuOpen
                    ? "opacity 0.15s ease 0.15s, transform 0.2s ease 0.15s"
                    : "opacity 0.1s ease 0.12s, transform 0.15s ease 0.12s",
                }}
              />

              {/* Screenshot */}
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  getScreenshot()
                  setIsMobileMenuOpen(false)
                }}
                className="ay-flex ay-items-center ay-justify-center ay-w-11 ay-h-11 ay-rounded-full hover:ay-bg-gray-100"
                style={{
                  opacity: isMobileMenuOpen ? 1 : 0,
                  transform: isMobileMenuOpen ? "scale(1)" : "scale(0)",
                  transition: isMobileMenuOpen
                    ? "opacity 0.2s ease 0.10s, transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) 0.10s"
                    : "opacity 0.15s ease 0.18s, transform 0.2s ease 0.18s",
                }}
              >
                <img
                  src={icon_screenshot}
                  alt="Screenshot"
                  className="ay-w-5 ay-h-5"
                />
              </button>
            </div>

            {/* Bottom toggle area - white when closed, black when open */}
            <div
              className="ay-flex ay-items-center ay-justify-center ay-flex-shrink-0 ay-rounded-full"
              style={{
                width: "56px",
                height: "56px",
                backgroundColor: isMobileMenuOpen ? "#000" : "#fff",
                transition: isMobileMenuOpen
                  ? "background-color 0.25s ease 0.1s"
                  : "background-color 0.25s ease 0.35s",
              }}
            >
              {/* Hamburger icon */}
              <svg
                className="ay-w-5 ay-h-5 ay-absolute"
                style={{
                  opacity: isMobileMenuOpen ? 0 : 1,
                  transform: isMobileMenuOpen
                    ? "rotate(90deg) scale(0.5)"
                    : "rotate(0deg) scale(1)",
                  transition: isMobileMenuOpen
                    ? "opacity 0.2s ease 0s, transform 0.3s cubic-bezier(0.4, 0, 0.2, 1) 0s"
                    : "opacity 0.25s ease 0.45s, transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1) 0.4s",
                }}
                fill="none"
                stroke="#4b5563"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
                />
              </svg>
              {/* X icon */}
              <svg
                className="ay-w-5 ay-h-5 ay-absolute"
                style={{
                  opacity: isMobileMenuOpen ? 1 : 0,
                  transform: isMobileMenuOpen
                    ? "rotate(0deg) scale(1)"
                    : "rotate(-90deg) scale(0.5)",
                  transition: isMobileMenuOpen
                    ? "opacity 0.25s ease 0.15s, transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1) 0.15s"
                    : "opacity 0.2s ease 0.1s, transform 0.3s cubic-bezier(0.4, 0, 0.2, 1) 0.05s",
                }}
                fill="none"
                stroke="#fff"
                viewBox="0 0 24 24"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>
    )
  },
)

export default PlannerPlayer
